/** Request body for backend POST /properties/create (AdminPropertyCreateSchema) */
export interface PropertyCreateRequestBody {
  name: string;
  description?: string | null;
  address?: string | null;
  city_id: number;
  original_price?: number | null;
  discounted_price?: number | null;
  total_rooms?: number | null;
  capacity?: number | null;
  highlight?: string | null;
  driveable_distance?: string | null;
  display: number;
  property_type: number[];
  property_category: number[];
  event_types?: number[];
  amenities?: number[];
  property_images: Array<{
    id?: number;
    image_url: string;
    image_scope: "PROPERTY" | "ROOM";
    category_id: number;
    is_primary: boolean;
    sort_order: number;
  }>;
}

/** Form validation result (before building API body) */
export interface PropertyCreationFormValues {
  name: string;
  description?: string;
  address?: string;
  city_id: number;
  original_price?: number;
  discounted_price?: number;
  total_rooms?: number;
  capacity?: number;
  highlight?: string;
  driveable_distance?: string;
  display: boolean;
  property_type_id?: number;
  property_category_id: number;
  event_type_ids: number[];
  amenity_ids: number[];
  property_images: File[];
}

export interface PropertyFormPayload {
  name: string;
  description?: string;
  address?: string;
  city_id: string | null;
  original_price: string | number;
  discounted_price: string | number;
  total_rooms: string | number;
  capacity: string | number;
  highlight?: string;
  driveable_distance: string;
  display: boolean;
  property_type_id: string | null;
  property_category_id: string | null;
  event_type_ids: string[];
  amenity_ids: string[];
  property_images: File[];
}

function toNum(val: string | number): number | undefined {
  if (val === "" || val === undefined || val === null) return undefined;
  const n = typeof val === "string" ? Number(val) : val;
  return Number.isNaN(n) ? undefined : n;
}

function positiveOptional(
  val: string | number
): number | undefined {
  const n = toNum(val);
  return n === undefined ? undefined : n > 0 ? n : undefined;
}

export function validatePropertyForm(
  payload: PropertyFormPayload,
  options?: { existingImageCount?: number }
): { valid: true; data: PropertyCreationFormValues } | { valid: false; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const existingCount = options?.existingImageCount ?? 0;
  const newCount = payload.property_images?.length ?? 0;
  const totalImages = existingCount + newCount;

  const name = (payload.name ?? "").trim();
  if (!name) errors.name = "Name is required";

  if (payload.city_id === "" || payload.city_id === null || payload.city_id === undefined) {
    errors.city_id = "City is required";
  }

  const originalPrice = toNum(payload.original_price);
  if (originalPrice !== undefined && originalPrice < 0) {
    errors.original_price = "Must be 0 or greater";
  }
  const discountedPrice = toNum(payload.discounted_price);
  if (discountedPrice !== undefined && discountedPrice < 0) {
    errors.discounted_price = "Must be 0 or greater";
  }

  const totalRooms = positiveOptional(payload.total_rooms);
  if (
    payload.total_rooms !== "" &&
    payload.total_rooms !== undefined &&
    toNum(payload.total_rooms) !== undefined &&
    totalRooms === undefined
  ) {
    errors.total_rooms = "Must be a positive number";
  }

  const capacityNum = toNum(payload.capacity);
  if (capacityNum !== undefined && capacityNum < 0) {
    errors.capacity = "Must be 0 or greater";
  }
  const capacity = capacityNum !== undefined && capacityNum >= 0 ? capacityNum : undefined;

  const driveableDistanceStr = typeof payload.driveable_distance === "string" ? payload.driveable_distance.trim() : String(payload.driveable_distance ?? "");
  if (driveableDistanceStr.length > 100) {
    errors.driveable_distance = "Max 100 characters";
  }

  if (!payload.property_category_id || payload.property_category_id === "") {
    errors.property_category_id = "At least one property category is required";
  }

  if (totalImages < 5) {
    errors.property_images = "Minimum 5 images are required (existing + new)";
  }

  if (Object.keys(errors).length > 0) {
    return { valid: false, errors };
  }

  const data: PropertyCreationFormValues = {
    name,
    description: (payload.description ?? "").trim() || undefined,
    address: (payload.address ?? "").trim() || undefined,
    city_id: Number(payload.city_id),
    original_price: originalPrice,
    discounted_price: discountedPrice,
    total_rooms: totalRooms,
    capacity: capacity,
    highlight: (payload.highlight ?? "").trim() || undefined,
    driveable_distance: driveableDistanceStr || undefined,
    display: payload.display,
    ...(payload.property_type_id ? { property_type_id: Number(payload.property_type_id) } : {}),
    property_category_id: Number(payload.property_category_id),
    event_type_ids: (payload.event_type_ids ?? []).map((id) => Number(id)),
    amenity_ids: (payload.amenity_ids ?? []).map((id) => Number(id)),
    property_images: payload.property_images,
  };

  return { valid: true, data };
}
